import {HttpClient} from '@angular/common/http';
import {ChangeDetectorRef, Component, ElementRef, inject, Inject, OnInit, PLATFORM_ID,
  CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import {CheckoutSupportWidgetComponent} from '@app/checkout/checkout-support-widget/checkout-support-widget.component';
import {CmsFlexComponentData} from '@app/checkout/order-confirmation/order-confirmation-list-item/order-confirmation-order-item.model';
import {EddlFormViewEvent} from '@app/services/analytics/EDDL/events/eddl-form-view-event';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {DcpSupportFormService} from '@app/shared/support-form/dcp-support-form.service';
import {
  ChatAdvisorPageService,
  CommercialModelService,
  ConfigService,
  DcpGlobalMessageService,
  ICommercialModelConfig,
  JsonService,
} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {EventService, LanguageService, Product, TranslatePipe} from '@spartacus/core';
import {CmsComponentData, CurrentProductService} from '@spartacus/storefront';
import {Observable} from 'rxjs';
import {take, map} from 'rxjs/operators';
import {DcpProductUtilitiesService} from '../../services/dcp-product-utilities.service';
import {PARTNER_SLM_TRANSACTION_TYPE} from '../publisher/publisher.component';
import {LuigiContainer} from '@luigi-project/container';
import {FormDataTrackingService} from '@app/services/analytics/form-data-tracking.service';
import {LeadFormLuigiContext} from '@app/product/models/lead.model';
import {DcpLeadFormModalComponent} from './lead-form-modal/dcp-lead-form-modal.component';
import {CampaignTrackingService} from '@app/services/campaign-tracking.service';
import {OAuthService} from 'angular-oauth2-oidc';
import {BreakpointObserver} from '@angular/cdk/layout';
import {NgTemplateOutlet, AsyncPipe} from '@angular/common';
import {DcpEddlFormViewService, LEAD_FORM_ACTION_TYPE} from '@app/services/analytics/EDDL/dcp-eddl-form-view.service';

@UntilDestroy()
@Component({
  selector: 'dcp-product-support-widget',
  templateUrl: './product-support-widget.component.html',
  styleUrls: ['./product-support-widget.component.scss'],
  imports: [NgTemplateOutlet, AsyncPipe, TranslatePipe, DcpLeadFormModalComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProductSupportWidgetComponent extends CheckoutSupportWidgetComponent implements OnInit {
  isAgentAvailable: boolean = true;
  isSapProduct: boolean;
  isProductDetailsPage: boolean;
  public product: Product;
  public partnerCommercialModelConfig: ICommercialModelConfig;
  isSupportWidgetOpen: boolean = false;
  showSupportWidget$: Observable<boolean>;

  public modalId: string = 'modal-lead-form';
  public container: LuigiContainer;
  public leadFormContext: LeadFormLuigiContext;
  private currentLanguage: string = '';

  private chatAdvisorPageService: ChatAdvisorPageService = inject(ChatAdvisorPageService);
  private elementRef: ElementRef = inject(ElementRef);

  constructor(
    @Inject(PLATFORM_ID) platformId: string,
    public cdr: ChangeDetectorRef,
    cmsData: CmsComponentData<CmsFlexComponentData>,
    jsonService: JsonService,
    countrySelectorService: CountrySelectorService,
    private supportFormService: DcpSupportFormService,
    private currentProductService: CurrentProductService,
    public dcpProductUtilitiesService: DcpProductUtilitiesService,
    private http: HttpClient,
    private dcpGlobalMessageService: DcpGlobalMessageService,
    private configService: ConfigService,
    private eventService: EventService,
    public formDataTrackingService: FormDataTrackingService,
    public breakpointObserver: BreakpointObserver,
    private commercialModelService: CommercialModelService,
    private dcpEddlFormViewService: DcpEddlFormViewService,
    private campaignTrackingService: CampaignTrackingService,
    private languageService: LanguageService,
    private oAuthService: OAuthService,
  ) {
    super(platformId, cmsData, jsonService, countrySelectorService, supportFormService, breakpointObserver);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.commercialModelService
      .getCommercialModelConfig(PARTNER_SLM_TRANSACTION_TYPE.code)
      .pipe(untilDestroyed(this))
      .subscribe((commercialModelConfig: ICommercialModelConfig) => {
        this.partnerCommercialModelConfig = commercialModelConfig;
      });
    this.languageService
      .getActive()
      .pipe(untilDestroyed(this))
      .subscribe((language: string) => {
        this.currentLanguage = language;
        if (this.product) {
          this.leadFormContext = this.buildLeadFormContext(this.product);
        }
      });
    this.currentProductService
      .getProduct()
      .pipe(untilDestroyed(this))
      .subscribe((product: Product) => {
        this.isProductDetailsPage = !!product;
        if (product) {
          this.product = product;
          this.isSapProduct = this.dcpProductUtilitiesService.isSapProduct(this.product);
          this.leadFormContext = this.buildLeadFormContext(this.product);
        }
      });
    this.http
      .get<{[key: string]: boolean}>(this.configService.getString('chatAgentAvailable.baseUrl'), {
        params: this.configService.getObject('chatAgentAvailable.params'),
      })
      .pipe(untilDestroyed(this), take(1))
      .subscribe({
        next: (data) => {
          this.isAgentAvailable = this.agentAvailable(data);
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.dcpGlobalMessageService.addMessage(error, this?.constructor?.name, 'errorLogging');
        },
      });

    // Show the support (Contact Us) widget whenever Joule is not shown. Gate on showChatAdvisor$
    // (which respects the master toggle), not advisorActiveOnPage$ (which ignores it) — otherwise a
    // page whose sub-toggle is ON while the master toggle is OFF would hide both Joule and Contact Us.
    this.showSupportWidget$ = this.chatAdvisorPageService.showChatAdvisor$.pipe(map((shown) => !shown));
  }

  openLeadRfiForm(): void {
    this.triggerEddlFormViewEvent();
    this.supportFormService.open();
  }

  openEddlModal() {
    const cont: LuigiContainer = document.querySelector('#dcp-lead-form-modal');
    const context: LeadFormLuigiContext = cont?.context as unknown as LeadFormLuigiContext;
    if (context?.modalId) {
      this.modalId = context?.modalId;
    }

    window.dispatchEvent(new CustomEvent(`dcp-lead-form:modal:open:${this.modalId}`));

    this.triggerViewTrackingCall();

    const adobeFormType = this.partnerCommercialModelConfig?.adobeFormType || 'WEB';
    const actionType = this.partnerCommercialModelConfig?.actionType || ''
    const name = this.formDataTrackingService.generateFormName(adobeFormType, this.product);
    const productId = this.dcpProductUtilitiesService.getProductId(this.product);
    this.dcpEddlFormViewService.triggerEddlFormViewEvent(actionType, name, this.product, productId);
  }

  private buildLeadFormContext(product: Product): LeadFormLuigiContext {
    return {
      idToken: this.oAuthService.getAccessToken(),
      modalId: this.modalId,
      productCode: this.dcpProductUtilitiesService.getProductCode(product),
      technicalName: this.dcpProductUtilitiesService.getProductId(product),
      action: PARTNER_SLM_TRANSACTION_TYPE.code,
      buttonText: PARTNER_SLM_TRANSACTION_TYPE.name,
      campaignData: this.campaignTrackingService.getCampaignData(),
      languageIso: this.currentLanguage,
    };
  }

  private triggerEddlFormViewEvent(): void {
    if (this.partnerCommercialModelConfig?.actionType === LEAD_FORM_ACTION_TYPE) {
      const event = new EddlFormViewEvent();
      event.name = PARTNER_SLM_TRANSACTION_TYPE.name;
      this.eventService.dispatch(event);
    }
  }

  private agentAvailable(statuses: {[key: string]: boolean}): boolean {
    return !!statuses && Object.values(statuses).some(Boolean);
  }

  toggleSupportWidget() {
    this.isSupportWidgetOpen = !this.isSupportWidgetOpen;
    this.cdr.detectChanges();
    if (this.isSupportWidgetOpen) {
      const firstButton = this.elementRef.nativeElement.querySelector('.support-widget__content button');
      firstButton?.focus();
    }
  }

  private triggerViewTrackingCall() {
    const adobeFormType = this.partnerCommercialModelConfig?.adobeFormType || 'WEB';
    const name = this.formDataTrackingService.generateFormName(adobeFormType, this.product);
    this.formDataTrackingService.trackFormView(
      name,
      undefined,
      this.product,
    );
  }
}
